import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Plus, Search } from "lucide-react";
import {
  Body,
  Caption,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VButton,
  VDataTable,
  VInput,
  VSelect,
  type VDataTableColumn,
} from "@/components/ui";
import { useCurrentRole, useCurrentUser } from "@/domains/auth";
import {
  useRequests,
  useRequestsByRequester,
} from "@/domains/requests/api/queries";
import type { CameraRequest, RequestStatus } from "@/domains/requests/api/types";
import { RequestStatusBadge } from "@/domains/requests/components/requestStatusBadge";

const STATUS_OPTIONS: ReadonlyArray<{ value: RequestStatus | "all"; label: string }> = [
  { value: "all", label: "Alle statussen" },
  { value: "draft", label: "Concept" },
  { value: "submitted", label: "Ingediend" },
  { value: "inReview", label: "In behandeling" },
  { value: "awaitingClarification", label: "Wacht op verduidelijking" },
  { value: "approved", label: "Goedgekeurd" },
  { value: "rejected", label: "Afgewezen" },
  { value: "completed", label: "Afgerond" },
];

function parseStatusFilter(value: string): RequestStatus | "all" {
  return STATUS_OPTIONS.find((o) => o.value === value)?.value ?? "all";
}

export function RequestListPage(): React.JSX.Element {
  const navigate = useNavigate();
  const role = useCurrentRole();
  const currentUser = useCurrentUser();
  const isRequester = role === "requester";

  const queryForRequester = useRequestsByRequester(currentUser.id);
  const queryForAll = useRequests();
  const { data, isPending, isError } = isRequester ? queryForRequester : queryForAll;

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<RequestStatus | "all">("all");

  const filtered = useMemo<ReadonlyArray<CameraRequest>>(() => {
    const list = data ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (!q) return true;
      return (
        r.title.toLowerCase().includes(q) ||
        r.number.toLowerCase().includes(q) ||
        r.location.address.toLowerCase().includes(q)
      );
    });
  }, [data, search, status]);

  const columns: ReadonlyArray<VDataTableColumn<CameraRequest>> = [
    {
      key: "number",
      header: "Nummer",
      cell: (r) => <span className="font-semibold">{r.number}</span>,
    },
    {
      key: "title",
      header: "Titel",
      cell: (r) => (
        <div>
          <Body weight="medium">{r.title}</Body>
          <Caption tone="muted">{r.location.address}</Caption>
        </div>
      ),
    },
    {
      key: "requester",
      header: "Aanvrager",
      cell: (r) => (
        <div>
          <Body>
            {r.requester.firstName} {r.requester.lastName}
          </Body>
          <Caption tone="muted">{r.requester.service}</Caption>
        </div>
      ),
    },
    {
      key: "period",
      header: "Periode",
      cell: (r) => (
        <Caption tone="default">
          {r.period.startDate} → {r.period.endDate}
        </Caption>
      ),
    },
    {
      key: "submittedAt",
      header: "Ingediend",
      cell: (r) => <Caption tone="muted">{r.submittedAt.slice(0, 10)}</Caption>,
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => <RequestStatusBadge status={r.status} />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={isRequester ? "Mijn aanvragen" : "Alle aanvragen"}
        subtitle={
          isRequester
            ? "Overzicht van de camera-aanvragen die je hebt ingediend."
            : "Inkomende aanvragen voor mobiel cameratoezicht."
        }
        actions={
          <VButton asChild>
            <Link to="/requests/new">
              <Plus className="size-4" aria-hidden />
              Nieuwe aanvraag
            </Link>
          </VButton>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full min-w-[240px] max-w-sm flex-1 sm:w-auto">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <VInput
            placeholder="Zoek op titel, nummer of adres…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="w-[200px]">
          <VSelect
            value={status}
            onChange={(e) => setStatus(parseStatusFilter(e.target.value))}
            aria-label="Filter op status"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </VSelect>
        </div>
      </div>

      {isPending ? (
        <LoadingState label="Aanvragen laden…" />
      ) : isError ? (
        <ErrorMessage message="Kon aanvragen niet ophalen." />
      ) : (
        <VDataTable
          columns={columns}
          rows={filtered}
          getRowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/requests/${r.id}`)}
          emptyState={
            <EmptyState
              title="Geen aanvragen gevonden"
              description="Start een nieuwe aanvraag of pas je filters aan."
              action={
                <VButton asChild>
                  <Link to="/requests/new">
                    <Plus className="size-4" aria-hidden />
                    Nieuwe aanvraag
                  </Link>
                </VButton>
              }
            />
          }
        />
      )}
    </div>
  );
}
